import { exportCompanyChartReport } from '@commenda/api';
import { formatLongDate, sanitizeFilename } from '@commenda/utils';
import { type ReactFlowInstance, type Rect } from '@xyflow/react';
import { toPng } from 'html-to-image';
import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import {
  captureChart,
  ClipboardUnsupportedError,
  getCaptureStyleProperties,
  type Capture,
} from '../exports/exportOrgChart';
import { TaxChartLegend } from './components/TaxChartLegend';

// Mirrors exportOrgChart.BG so the clipboard / PDF variants land on the
// same cream background as the canvas wrapper's `bg-bg-light` token.
const BG = '#fcfbf8';

// Inset between the legend and the chart-PNG edges. Picked so the legend sits
// inside the chart's PADDING (80 in exportOrgChart) without crowding nodes.
const LEGEND_INSET = 24;

const buildFilename = (
  companyName: string | undefined,
  ext: 'png' | 'pdf',
): string => {
  const base = sanitizeFilename(companyName, 'Tax');
  return `${base} - Tax Structure - ${formatLongDate()}.${ext}`;
};

const triggerDownload = (dataUrl: string, filename: string): void => {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('Failed to load image for compositing'));
    img.src = url;
  });

// Renders the static (chevron-less, always-expanded) legend into a hidden
// offscreen host, captures it as a PNG, and tears the mount down. Same
// `createRoot` pattern that captureCompanyCharts.ts uses for the diagram.
async function renderStaticLegendToPng(
  pixelRatio: number,
): Promise<{ dataUrl: string; cssWidth: number; cssHeight: number }> {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-99999px',
    'top:0',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(host);

  const root = createRoot(host);

  const cleanup = () => {
    queueMicrotask(() => {
      try {
        root.unmount();
      } catch {
        /* ignore */
      }
      host.remove();
    });
  };

  try {
    // Commit now: after a long chart capture Firefox can run both frames below before React's scheduled render.
    flushSync(() => root.render(createElement(TaxChartLegend, { variant: 'static' })));
    // Two RAFs to let the browser paint before we measure.
    await new Promise<void>((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => r())),
    );
    // Fonts may still be in-flight on the first capture after a hard reload.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      await document.fonts.ready;
    }
    const inner = host.firstElementChild as HTMLElement | null;
    if (!inner) throw new Error('Legend mount produced no element');
    const cssWidth = inner.offsetWidth;
    const cssHeight = inner.offsetHeight;
    const dataUrl = await toPng(inner, {
      backgroundColor: undefined,
      pixelRatio,
      includeStyleProperties: getCaptureStyleProperties(),
    });
    return { dataUrl, cssWidth, cssHeight };
  } finally {
    cleanup();
  }
}

async function compositeLegendAtBottomLeft(
  base: Capture,
  bgColor: string | undefined,
  legend: { dataUrl: string; cssWidth: number; cssHeight: number },
): Promise<Capture> {
  const [baseImg, legendImg] = await Promise.all([
    loadImage(base.dataUrl),
    loadImage(legend.dataUrl),
  ]);
  const pixelRatio = baseImg.naturalWidth / base.domWidth;
  const inset = LEGEND_INSET * pixelRatio;
  const stripHeight = legendImg.naturalHeight + 2 * inset;

  const canvas = document.createElement('canvas');
  canvas.width = baseImg.naturalWidth;
  canvas.height = baseImg.naturalHeight + stripHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  if (bgColor) {
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(baseImg, 0, 0);
  const x = inset;
  const y = baseImg.naturalHeight + inset;
  ctx.drawImage(legendImg, x, y);
  return {
    dataUrl: canvas.toDataURL('image/png'),
    domWidth: base.domWidth,
    domHeight: base.domHeight + stripHeight / pixelRatio,
  };
}

// Captures the tax chart and composites the static legend at bottom-left.
// `container` scopes the viewport lookup so an offscreen mount doesn't
// accidentally grab the live on-screen diagram (and vice versa).
// `contentBounds` frames everything the structure chart draws, not only the nodes.
export async function captureTaxChart(
  rf: ReactFlowInstance,
  backgroundColor: string | undefined,
  container?: HTMLElement,
  contentBounds?: Rect,
): Promise<Capture> {
  const chartCapture = await captureChart(rf, backgroundColor, container, contentBounds);
  // Match the chart's effective pixel ratio so the legend stays crisp at the
  // same density (avoids a soft-looking legend on a sharp chart).
  const baseImg = await loadImage(chartCapture.dataUrl);
  const pixelRatio = baseImg.naturalWidth / chartCapture.domWidth;
  const legend = await renderStaticLegendToPng(pixelRatio);
  return compositeLegendAtBottomLeft(chartCapture, backgroundColor, legend);
}

export async function exportTaxChartPng(
  rf: ReactFlowInstance,
  companyName?: string,
  contentBounds?: Rect,
): Promise<void> {
  const { dataUrl } = await captureTaxChart(rf, undefined, undefined, contentBounds);
  triggerDownload(dataUrl, buildFilename(companyName, 'png'));
}

export async function copyTaxChartPngToClipboard(
  rf: ReactFlowInstance,
  contentBounds?: Rect,
): Promise<void> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
    throw new ClipboardUnsupportedError();
  }
  // BG (not transparent) — the system clipboard doesn't reliably preserve PNG
  // alpha, so alpha captures paste as black blocks in Slack/Notion/etc.
  // Safari only allows the write while the click is being handled.
  const png = captureTaxChart(rf, BG, undefined, contentBounds)
    .then(({ dataUrl }) => fetch(dataUrl))
    .then((response) => response.blob());
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
}

export async function exportTaxChartPdf(
  rf: ReactFlowInstance,
  companyId: number,
  companyName?: string,
  contentBounds?: Rect,
): Promise<void> {
  const { dataUrl } = await captureTaxChart(rf, undefined, undefined, contentBounds);
  await exportCompanyChartReport(companyId, 'tax-chart', companyName, dataUrl);
}
